import React from "react";
import { Box, Grid2, Typography } from "@mui/material";
import EnergyConsumptionKPISection from "./EnergyConsumptionKPISection";
import EnergyConsumptionBreakdownChart from "./EnergyConsumptionBreakdownChart";
import EnergyConsumptionTrends, { EnergyConsumptionDetailsView } from "./EnergyConsumptionTrends";
import EnergyFlowAnalysis from "./EnergyFlowAnalysis";
import { EnergyFlowDetailsView } from "./EnergyFlowTrends";
import CartLoader from "../../../Helper/Loaders/CartLoader";
import { useBaseEntityInfo } from "../../../Hooks/usePagesContent";
import AggregatedMetricsTable from "./AggregatedMetricsTable";
import { calculateGranularity } from "../Components/FleetCommonFunctions";

const EnergyConsumption = ({
  startDate,
  endDate,
  vehiclesConnected,
  isLoadingVehiclesCount,
  globalFilters = {},
  allFleets,
  allOnboardingLocationsHierarchy,
  showChartDetails,
  selectedChartType,
  selectedChartGranularity,
  onShowDetails,
}) => {
  const {
    baseEntityTypePluralLable,
  } = useBaseEntityInfo();

  if (showChartDetails && selectedChartType === "ENERGY_CONSUMPTION_PER_KM") {
    return (
      <Box sx={{ width: "100%" }}>
        <EnergyConsumptionDetailsView
          chartType={selectedChartType}
          onBack={() => onShowDetails?.(false, null)}
          startDate={startDate}
          endDate={endDate}
          initialGranularity={selectedChartGranularity}
          allFleets={allFleets}
          allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
          globalFilters={globalFilters}
        />
      </Box>
    );
  }

  if (showChartDetails && selectedChartType === "ENERGY_FLOW_TRENDS") {
    return (
      <Box sx={{ width: "100%" }}>
        <EnergyFlowDetailsView
          onBack={() => onShowDetails?.(false, null)}
          startDate={startDate}
          endDate={endDate}
          initialGranularity={selectedChartGranularity}
          allFleets={allFleets}
          allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
          globalFilters={globalFilters}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Grid2 container alignItems="center" justifyContent="space-between">
        <Box>
          <Typography
            variant="fw400s20c000"
            sx={{
              lineHeight: "28px",
              fontWeight: 300,
            }}
          >
            Key Performance Indicators
          </Typography>
        </Box>
        {isLoadingVehiclesCount ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "16px",
              padding: "8px 16px",
            }}
          >
            <CartLoader />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              padding: "8px 16px",
              borderRadius: "8px",
            }}
          >
            <Typography
              variant="fw400s16c000000"
              sx={{
                color: "#444B51",
                lineHeight: "16px",
              }}
            >
              Connected {baseEntityTypePluralLable} Today :
            </Typography>
            <Typography
              variant="fw400s16c000000"
              sx={{
                fontWeight: 600,
                color: "#2E2E2E",
                lineHeight: "16px",
              }}
            >
              {vehiclesConnected}
            </Typography>
          </Box>
        )}
      </Grid2>

      {/* KPI Section */}
      <EnergyConsumptionKPISection
        startDate={startDate}
        endDate={endDate}
        globalFilters={globalFilters}
        vehiclesConnected={vehiclesConnected}
        showHeader={false}
      />

      <Box>
        <Typography
          variant="fw400s20c000"
          sx={{
            lineHeight: "28px",
            fontWeight: 300,
          }}
        >
          Energy Consumption per km
        </Typography>
      </Box>

      {/* Energy Consumption Breakdown Chart */}
      <EnergyConsumptionBreakdownChart
        startDate={startDate}
        endDate={endDate}
        globalFilters={globalFilters}
      />
      <EnergyConsumptionTrends
        onShowDetails={onShowDetails}
        startDate={startDate}
        endDate={endDate}
        globalFilters={globalFilters}
      />

      {/* Energy Flow Analysis */}
      <EnergyFlowAnalysis
        startDate={startDate}
        endDate={endDate}
        globalFilters={globalFilters}
        onShowDetails={onShowDetails ? () => onShowDetails(true, "ENERGY_FLOW_TRENDS", calculateGranularity(startDate, endDate)) : undefined}
      />

      {/* Aggregated Metrics Table */}
      <AggregatedMetricsTable
        startDate={startDate}
        endDate={endDate}
        globalFilters={globalFilters}
      />
    </Box>
  );
};

export default EnergyConsumption;
